import { Head, router } from '@inertiajs/react'
import confetti from 'canvas-confetti'
import React, { useEffect, useRef, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import Button from '../../Components/Button'
import IconBadge from './Components/IconBadge'
import OrderInfoCard from './Components/OrderInfoCard'
import PhoneButton from '../../Components/PhoneButton'
import ShareButton from './Components/ShareButton'
import Layout from './Layout'
import { formatPrice, formatTime } from './utils/formatters'

export default function Confirmation({ account, order }) {
  const [splitCount, setSplitCount] = useState(1)
  const orderCardRef = useRef(null)

  const perPerson = Math.ceil(order.total_pence / splitCount)

  // Fire confetti on mount, respecting motion preferences
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) return

    const duration = 2000
    const end = Date.now() + duration

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 }
      })
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 }
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }

    frame()
  }, [])

  // Poll for order status updates
  useEffect(() => {
    const interval = setInterval(() => {
      router.reload({ only: ['order'] })
    }, 5000) // Poll every 5 seconds

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <Head title={`Order Confirmed - ${account.name}`} />
      <Layout account={account}>
        <div className="min-h-screen flex flex-col items-center justify-center p-6">
        <IconBadge label="Order confirmed" color="bg-green-500" className="my-6">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </IconBadge>

        <h1 className="text-3xl font-bold text-white text-center mb-2">
          We're on it, see you soon
        </h1>

        {order.order_code && (
          <div className="bg-white rounded-2xl p-6 mb-6 w-full max-w-sm text-center" role="region" aria-label="Order code">
            <p className="text-sm text-gray-500 mb-1">Your order code</p>
            <p className="text-3xl font-extrabold text-sky-900 tracking-wide">{order.order_code}</p>
            <p className="text-xs text-gray-400 mt-2">Give this to staff when collecting</p>
          </div>
        )}

        {order.table_number ? (
          <div className="mb-6 w-full max-w-sm" role="region" aria-label="Delivery table">
            <OrderInfoCard subtitle="We'll bring it to">
              Table {order.table_number}
            </OrderInfoCard>
          </div>
        ) : (
          <div className="mb-6 w-full max-w-sm" role="region" aria-label="Order ready time">
            <OrderInfoCard subtitle="Your order will be ready for">
              {formatTime(order.pickup_time)}
            </OrderInfoCard>
          </div>
        )}

        <section
          ref={orderCardRef}
          className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 w-full max-w-sm text-white"
          aria-label="Order details"
        >
          {order.order_code && (
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm opacity-80">Order Code</span>
              <span className="font-bold">{order.order_code}</span>
            </div>
          )}
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm opacity-80">Order Number</span>
            <span className="font-mono text-sm opacity-80">{order.order_number}</span>
          </div>

          <ul aria-label="Order items">
            {order.order_items.map(item => (
              <li key={item.id} className="flex justify-between py-2 border-b border-white/20">
                <span>
                  {item.quantity}x {item.menu_item.name}
                  {item.add_ons && item.add_ons.length > 0 && (
                    <span className="block text-sm opacity-80">
                      {item.add_ons.map(a => `+ ${a.name}`).join(', ')}
                    </span>
                  )}
                </span>
                <span>{formatPrice(item.line_total_pence)}</span>
              </li>
            ))}
          </ul>

          <div className="flex justify-between pt-4 font-bold text-lg">
            <span>Total</span>
            <span>{formatPrice(order.total_pence)}</span>
          </div>
        </section>

        {/* Split Bill Helper */}
        <section className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 w-full max-w-sm text-white" aria-label="Split the bill">
          <h2 className="text-lg font-bold mb-4 text-center">Split the bill</h2>
          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={() => setSplitCount(Math.max(1, splitCount - 1))}
              disabled={splitCount <= 1}
              variant="ghost"
              theme="dark"
              circular
              className="w-12 h-12 text-2xl"
              ariaLabel="Decrease number of people"
            >
              -
            </Button>
            <div className="text-center min-w-[100px]" aria-live="polite" aria-atomic="true">
              <div className="text-3xl font-bold">{splitCount}</div>
              <div className="text-sm opacity-80">{splitCount === 1 ? 'person' : 'people'}</div>
            </div>
            <Button
              onClick={() => setSplitCount(splitCount + 1)}
              variant="ghost"
              theme="dark"
              circular
              className="w-12 h-12 text-2xl"
              ariaLabel="Increase number of people"
            >
              +
            </Button>
          </div>
          <div className="mt-4 text-center" aria-live="polite" aria-atomic="true">
            <div className="text-2xl font-bold">{formatPrice(perPerson)} each</div>
          </div>
        </section>

        {/* Share Order */}
        <div className="w-full max-w-sm mb-6">
          <ShareButton
            orderRef={orderCardRef}
            order={order}
            account={account}
            onSuccess={() => toast.success('Order shared!')}
            onCopySuccess={() => toast.success('Order details copied to clipboard')}
            onError={() => toast.error('Failed to share order')}
          />
        </div>

        <p className="text-white/80 text-center mb-6 max-w-sm">
          An email has been sent to <strong>{order.email}</strong> with your order summary
        </p>

        {account.phone && (
          <div className="w-full max-w-sm mb-4">
            <PhoneButton
              phoneNumber={account.phone}
              className="shadow-lg"
              ariaLabel={`Call clubhouse at ${account.phone}`}
            >
              Call Clubhouse: {account.phone}
            </PhoneButton>
          </div>
        )}

        {account.contact_email && (
          <div className="w-full max-w-sm mb-4">
            <Button
              onClick={() => window.location.href = `mailto:${account.contact_email}`}
              variant="secondary"
              theme="dark"
              size="lg"
              fullWidth
              className="shadow-lg"
              ariaLabel={`Email clubhouse at ${account.contact_email}`}
            >
              Email Clubhouse
            </Button>
          </div>
        )}

        <a
          href={`/${account.slug}/order`}
          className="text-white/80 hover:text-white underline focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-transparent rounded px-2 py-1"
          aria-label="Order more items"
        >
          Order more
        </a>
      </div>
        <Toaster position="bottom-center" />
      </Layout>
    </>
  )
}
